import React, { useEffect, useRef, useState } from 'react';
import ImgStack from './imgStack';
import { Timestamp } from 'firebase/firestore';
import { Add, Cancel, Done, Edit, Exit } from './Icons';
import { uploadImage, updateData, deleteDocument } from '../functions/firebaseFunctions';
import Error from './Error';
import CodeSnippet from './CodeSnippet';

const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
    const day = date.getDate().toString().padStart(2, '0'); // Get day of month with leading zero
    const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date); // Get abbreviated month name
    const year = date.getFullYear(); // Get full year
    const formattedDate = `${day}-${month}-${year}`; // Combine day, month, and year into formatted date string

    const hours = date.getHours() > 12 ? date.getHours() - 12 : date.getHours();
    const minutes = date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes();
    const ampm = date.getHours() >= 12 ? 'pm' : 'am';
    const formattedTime = `${hours}:${minutes} ${ampm}`;


    return (`${formattedDate} - ${formattedTime}`); // Output: "16-Feb-2023  06:20 pm"
}
const formatDateOnly = (timestamp) => {
    const date = timestamp ? new Date(timestamp * 1000) : new Date(); // Convert seconds to milliseconds
    const day = date.getDate().toString().padStart(2, '0'); // Get day of month with leading zero
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Get day of month with leading zero
    const year = date.getFullYear(); // Get full year
    const formattedDate = `${year}-${month}-${day}`; // Combine day, month, and year into formatted date string

    return (formattedDate); // Output: "2023-02-16"
}

const formatTimeOnly = (timestamp) => {
    const date = timestamp ? new Date(timestamp * 1000) : new Date(); // Convert seconds to milliseconds
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const formattedTime = `${hours}:${minutes}`;
    return (formattedTime); // Output: "23:15"
}

const ulStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBlockEnd: 0,
    marginBlockStart: 0,
    flexWrap: 'wrap',
    gap: 5,
    padding: 0
}

const liStyle = {
    marginBlockEnd: 0,
    listStyleType: 'none',
    padding: '5px 10px 1px',
    color: 'var(--orng)',
    fontSize: 16,
    borderWidth: '1px',
    borderRadius: 20,
    borderColor: 'var(--orng)',
    borderStyle: 'solid',
}

const outerDivStyle = {
    width: '100%',
    borderWidth: '2px',
    borderRadius: 10,
    borderColor: 'var(--orng)',
    borderStyle: 'solid',
    padding: '5px 15px',
    marginBottom: 20,
    //boxShadow: '3px 3px 5px var(--black50)',
}

const innerDivStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'center',
    borderWidth: '0 0 1px 0',
    borderColor: 'var(--white)',
    borderStyle: 'solid',
    justifyContent: 'space-between',
    paddingBottom: 5,
    marginBottom: 5
}

const textAreaStyle = {
    width: "100%",
    borderRadius: 10,
    padding: 10,
    fontSize: 15,
    marginTop: 5,
    height: "100%"
}
const buttonStyle = {
    backgroundColor: 'transparent',
    margin: 5,
    borderStyle: 'solid',
    borderWidth: 1,
}

export default function Entry({ entry, project, user, addEntry, updateEntry, deleteEntry, index }) {
    // for later const myTimestamp = Timestamp.fromDate(myDate);
    const descRef = useRef(null);
    const codeRef = useRef(null);
    // All  states are for editing data
    const [editing, setEditing] = useState(entry === 'new' ? true : false);
    const [error, setError] = useState(null);
    const [dateValue, setDateValue] = useState(formatDateOnly(entry?.date?.seconds));
    const [timeValue, setTimeValue] = useState(formatTimeOnly(entry?.date?.seconds));
    const [unselectedGoals, setUnselectedGoals] = useState([]);
    const [selectedGoals, setSelectedGoals] = useState([]);
    const [selectedImages, setSelectedImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const handleEntryImageUpload = async (event) => {
        setUploading(true);//show uploading status
        const newImages = Array.from(event.target.files);//extract the array of images
        Promise.all(newImages.map(async (image) => { // wait for everything to finish
            const imageURL = await uploadImage(project.projectId, image);//upload the image 
            return {
                name: image.name,// keep the name of the image 
                src: imageURL //save the url
            }
        })).then((updatedArray) => {
            setSelectedImages([...selectedImages, ...updatedArray]);//combine previously selected images with the new images
            setUploading(false);//stop showing uploading status
        })
    };
    const handleGoalSelect = (input) => {
        const value = input.target.value
        const updatedUnselectedGoals = unselectedGoals.filter(item => item !== value);//removes the value from unselected goals
        const updatedSelectedGoals = selectedGoals.concat(value);// adds value to selected goals
        input.target.value = "default" // reset the selector value back to the default
        setUnselectedGoals(updatedUnselectedGoals);
        setSelectedGoals(updatedSelectedGoals);
    }
    const handleGoalRemove = (value) => {
        const updatedUnselectedGoals = unselectedGoals.concat(value);// adds value to selected goals
        const updatedSelectedGoals = selectedGoals.filter(item => item !== value);//removes the value from unselected goals
        setUnselectedGoals(updatedUnselectedGoals);
        setSelectedGoals(updatedSelectedGoals);
    }
    const title2id = (title) => {
        return title.replace(/ /g, "_");
    }
    const par2text = (pars) => {
        return pars.join('\n');//Joints the parragraphs from an array into one text
    }
    const source2path = (source) => {
        const parts = source.split('/');
        const result = parts.slice(4).join('/');
        return (result)
    }

    function convertToEmbedUrl(url) {
        const segments = url.split("/");//split the link
        const videoId = segments[segments.length - 1];//the last bit is ID
        if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;//put ID at the end of an appropiate link
        } else {
            return null
        }
    }

    const handleSubmit = event => {
        setError(null);
        event.preventDefault();
        const target = event.target
        //copy basic strings
        const title = target.title.value;
        //this input should submit the sharable link to make it easier then we convert it to the embed link
        const video = convertToEmbedUrl(target.video.value);
        //split desc into array of paragraphs
        const desc = target.desc.value.split('\n');
        //combine date and time into onse string, then convert it into a firestore timestamp
        const date = Timestamp.fromDate(new Date(`${target.date.value}T${target.time.value}:00`));
        //add all the entry info into one object
        const snippet = {
            code: target?.code.value,
            source: {
                link: target?.codeSource.value,
                name: source2path(target?.codeSource.value),
            }
        }
        if (selectedGoals.length === 0) {
            setError({ code: "No Goals Selected", message: "Please select at lease one goal for this entry" })
        } else if (
            (target?.code.value && !target?.codeSource.value)
            ||
            (!target?.code.value && target?.codeSource.value)
        ) {
            setError({ code: "Code Source", message: "If entering code, enter the source as well" })
        } else {
            const entryObject = {
                title,
                date,
                ...(video !== null && { video }),
                desc,
                ...(selectedImages.length > 0 && { imgs: selectedImages }),
                goals: selectedGoals,
                snippet: snippet?.code ? snippet : null
            };
            //console.log("SubmitEvent", entryObject);
            const entryID = title2id(title)
            const adress = `projects/${project.projectId}/entries/${entry?.id || entryID}`
            updateData(adress, entryObject); // updates on the database

            if (entry === 'new') {
                entryObject.id = entryID // only needed for the local copy
                addEntry(entryObject); // adds entry to the local state
                // reset the states to the initial value
                setSelectedGoals([]);
                setSelectedImages([]);
                if (project?.goals) {
                    const goals = project.goals.map(obj => obj.name);
                    setUnselectedGoals(goals);
                }
                // reset the fields to the inital value
                event.target.title.value = null
                event.target.video.value = null
                event.target.desc.value = null
                event.target.code.value = null
                event.target.codeSource.value = null
            } else {
                updateEntry(index, entryObject) // localy update the entry
                setEditing(false); // returns the entry to static state
            }

        }
    }
    const handleDelete = () => {
        if (confirmDelete) { // if button says confirm then
            const adress = `projects/${project.projectId}/entries/${entry.id}`
            deleteDocument(adress)
            deleteEntry(index) // deletes the entry by index
        } else { // otherwise
            setConfirmDelete(true); // sets the button text to confirm
        }
    }

    useEffect(() => {
        if (project?.goals) {
            const goals = project.goals.map(obj => obj.name);
            setUnselectedGoals(goals);
        }
    }, [project]);

    useEffect(() => {
        //Adjusts the height of a text area when editing is enabled
        if (descRef.current) {
            descRef.current.style.height = `${descRef.current.scrollHeight}px`;
        }
        if (codeRef.current) {
            codeRef.current.style.height = `${codeRef.current.scrollHeight}px`;
        }
        setConfirmDelete(false)
    }, [editing]);
    if (editing) {
        return (
            <form style={outerDivStyle} onSubmit={handleSubmit}>
                {/* Top section with title, date, and goals */}
                <div style={innerDivStyle}>
                    <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 }}>
                        {/* Edit Title */}
                        <input
                            name="title"
                            required
                            className='h3'
                            placeholder={'New Entry Title'}
                            defaultValue={entry?.title && entry.title}
                        />
                        {/* Edit Date and time */}
                        <div style={{ display: 'inline-flex', gap: 10 }}>
                            <input name="date" type="date" id="date" required value={dateValue} style={{ fontSize: 14 }}
                                onChange={(input) => {
                                    setDateValue(input.target.value)
                                }} />
                            <input name="time" type="time" id="time" required value={timeValue} style={{ fontSize: 14 }}
                                onChange={(input) => {
                                    setTimeValue(input.target.value)
                                }} />
                        </div>
                    </div>
                    {/* Goal Selection */}
                    <ul style={ulStyle}>
                        {selectedGoals && selectedGoals.map(goal =>
                            <li style={{
                                marginBlockEnd: 0,
                                listStyleType: 'none'
                            }} key={goal}>
                                <button type="button" className="popUp"
                                    onClick={() => { handleGoalRemove(goal) }}
                                    style={{
                                        ...liStyle,
                                        backgroundColor: 'transparent'
                                    }}>
                                    {goal}
                                </button>
                            </li>
                        )}
                        <select defaultValue="default"
                            name="goals"
                            required
                            style={{
                                ...buttonStyle,
                                height: 32,
                                borderRadius: 16,
                                borderColor: "var(--orng)",
                                color: 'var(--orng)',
                                fontSize: 16,
                                padding: '0 8px'
                            }}
                            onChange={input => {
                                handleGoalSelect(input);
                            }}>
                            <option
                                value="default"
                                style={{
                                    textAlign: 'center',
                                    backgroundColor: 'var(--mdGray)',
                                    lineHeight: 2
                                }}
                                disabled
                            >Add +</option>
                            {unselectedGoals && unselectedGoals.map(goal =>
                                <option
                                    key={goal}
                                    value={goal}
                                    style={{
                                        textAlign: 'center',
                                        backgroundColor: 'var(--mdGray)',
                                        lineHeight: 2
                                    }}
                                >{goal}</option>
                            )}
                        </select>
                    </ul>
                </div>
                {/* Bottom section with main text, images and video */}
                <div style={{
                    display: 'grid',
                    alignItems: 'center',
                    gridTemplateColumns: 'auto 240px',
                    placeItems: 'center'
                }}>
                    {/* Left Section with video, description, and code */}
                    <div style={{
                        color: 'var(--white)',
                        display: 'grid',
                        placeItems: 'center',
                        width: "100%"
                    }} className="entryMain">
                        {/* Video */}
                        <input
                            name="video"
                            className='p'
                            style={{ width: '100%' }}
                            placeholder={'Enter YouTube Link'}
                            defaultValue={entry?.video && entry.video}
                        />
                        {/* Description */}
                        <textarea
                            required
                            ref={descRef}
                            name="desc"
                            style={textAreaStyle}
                            placeholder={'Enter New Entry Description'}
                            defaultValue={entry.desc && par2text(entry.desc)}
                            onInput={element => {
                                // This function adjust the height of the text area to fit all the text
                                element.target.style.height = element.target.scrollHeight + 'px'
                            }}
                        />
                        {/* Code */}
                        <hr style={{ borderColor: 'var(--orng)', marginBottom: 5 }} />
                        <input
                            name="codeSource"
                            className='p codeArea'
                            style={{ width: '100%' }}
                            placeholder={'Enter Code Source'}
                            defaultValue={entry?.snippet?.source?.link || undefined}
                        />
                        <textarea
                            name="code"
                            ref={codeRef}
                            style={textAreaStyle}
                            className='codeArea'
                            placeholder={'Enter Code'}
                            defaultValue={entry?.snippet?.code || undefined}
                            onInput={element => {
                                // This function adjust the height of the text area to fit all the text
                                element.target.style.height = element.target.scrollHeight + 'px'
                            }}
                        />
                    </div>
                    {/* Right Section with Images */}
                    <div className="entrySide">
                        {selectedImages.length > 0 &&
                            <ImgStack
                                images={selectedImages}
                                setImages={setSelectedImages}
                                editable
                            />}
                        <label htmlFor={entry.title + ' images upload'}
                            className='popUp btn' style={{
                                ...buttonStyle,
                                display: 'flex',
                                fontSize: 16,
                                transition: "500ms ease",
                                cursor: 'pointer',
                                width: 'auto'
                            }}>
                            {!uploading && <Add color='var(--orng)' size={16} />}
                            {uploading ? "Uploading ..." : "Add Images"}
                        </label>
                        <input
                            name={entry.title + ' Images Upload'}
                            disabled={uploading}
                            style={{ display: 'none' }}
                            type="file"
                            id={entry.title + ' images upload'}
                            multiple
                            accept="image/*"
                            onChange={handleEntryImageUpload}
                        />
                    </div>
                    {/* The lowest section with buttons*/}
                    <div style={{
                        gridColumn: "1/-1",
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        margin: '5px 0px'
                    }}>
                        {error && <Error error={error} />}
                        <button type="submit"
                            className='popUp btn'
                            style={{
                                ...buttonStyle,
                                display: 'flex',
                                borderColor: 'var(--orng)',
                                width: 'auto',
                                gap: 5,
                                height: 40,
                                alignItems: 'center'
                            }}>
                            Save
                            <Done color='var(--orng)' size={20} />
                        </button>
                        {entry !== 'new' && <button type="button"
                            className='popUp btn'
                            onClick={() => { setEditing(false) }}
                            style={{
                                ...buttonStyle,
                                borderColor: 'var(--orng)',
                                display: 'flex',
                                width: 'auto',
                                gap: 5,
                                height: 40,
                                alignItems: 'center'
                            }}>
                            Cancel
                            <Cancel color='var(--orng)' size={20} />
                        </button>}
                        {entry !== 'new' && <button type="button"
                            className='popUp btn'
                            onClick={handleDelete}
                            style={{
                                ...buttonStyle,
                                display: 'flex',
                                color: 'var(--red)',
                                width: 'auto',
                                borderColor: 'var(--red)',
                                gap: 5,
                                height: 40,
                                alignItems: 'center'
                            }}>
                            {confirmDelete ? "Confirm" : "Delete"}
                            <Exit color='var(--red)' size={20} />
                        </button>}
                    </div>

                </div>
            </form>
        )
    } else {
        return (
            <div style={outerDivStyle}>
                <div style={innerDivStyle}>
                    <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 }}>
                        <h3 style={{
                            color: 'var(--white)',
                            whiteSpace: 'nowrap',
                        }}>{entry?.title}</h3>
                        {entry?.date && <div style={{
                            color: 'var(--white50)',
                            display: 'flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                        }}>{formatDate(entry.date.seconds)}</div>}
                    </div>
                    {entry?.goals && <ul style={ulStyle}>
                        {entry.goals.map(goal => <li style={liStyle} key={goal}>{goal}</li>)}
                    </ul>}
                    {user && <button type='button' className='orangeIconBtn' onClick={() => {
                        if (entry.goals) {
                            //the following lines fill in the values for selected and unselected goals
                            setSelectedGoals(entry.goals)
                            const goals = project.goals.map(obj => obj.name);
                            const unslGoals = goals.filter((goal) => !entry.goals.includes(goal))
                            setUnselectedGoals(unslGoals);
                        }
                        if (entry.imgs) {
                            setSelectedImages(entry.imgs)
                        }
                        setEditing(true)
                    }}>
                        <Edit />
                    </button>}
                </div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 240px',
                }}>
                    <div style={{
                        color: 'var(--white)',
                        display: 'grid',
                    }} className={entry?.imgs ? "entryMain" : "entryFull"}>
                        {entry?.video &&
                            <iframe
                                className='youTube'
                                src={entry.video}
                                title="YouTube video player"
                                allow="
                                accelerometer; 
                                autoplay; 
                                clipboard-write; 
                                encrypted-media; 
                                gyroscope; 
                                picture-in-picture; 
                                web-share
                            "
                                allowFullScreen />
                        }
                        {entry?.desc && <div>
                            {entry.desc.map((p, i) => <p key={p.substring(0, 10) + i}>{p}</p>)}
                        </div>}
                        {entry?.snippet &&
                            <CodeSnippet snippet={entry?.snippet} />}
                    </div>
                    {/* Images */}
                    {entry?.imgs && <ImgStack images={entry.imgs} className="entrySide" />}
                </div>
            </div>
        )
    }

}